import React, { useState } from 'react';

const PdfSection = ({ dossier, onPdfPreview }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // 🐛 VERSION DEBUG - Toujours afficher la section
  console.log('🔍 PdfSection - Analyse du dossier:', dossier.id);
  console.log('🔍 Toutes les clés du dossier:', Object.keys(dossier));

  // Recherche tous les PDFs dans le dossier
  const findAllPdfs = () => {
    const pdfs = [];
    const allKeys = Object.keys(dossier);
    
    // 🐛 DEBUG - Afficher toutes les clés qui contiennent 'pdf' ou 'PDF'
    const pdfKeys = allKeys.filter(key => 
      key.toLowerCase().includes('pdf') || 
      key.toLowerCase().includes('declaration') ||
      key.toLowerCase().includes('controle')
    );
    
    console.log('🔍 Clés contenant PDF/declaration/controle:', pdfKeys);
    
    // Organiser par produit
    if (Array.isArray(dossier.produits)) {
      dossier.produits.forEach((product, productIndex) => {
        console.log(`🔍 Analyse produit ${productIndex}: ${product.name}`);
        
        const productPdfs = [];
        
        // 1. Déclaration CE (plusieurs formats possibles)
        const cePossibleKeys = [
          `declarationCE_${product.productId}`,
          `declarationCE_${product.uuid}`,
          `declarationCE_${product.id}`,
          `declarationCE_${productIndex}`,
          `declarationCE_${product.name?.toLowerCase().replace(/[^a-z0-9]/g, '')}`,
          `declarationCE_product_${productIndex}`
        ];
        
        cePossibleKeys.forEach(key => {
          if (dossier[key]) {
            console.log(`✅ Trouvé déclaration CE: ${key} = ${dossier[key]}`);
            productPdfs.push({
              type: 'ce',
              title: 'Déclaration de Conformité CE',
              url: dossier[key],
              status: 'sent',
              icon: '📧'
            });
          }
        });
        
        // 2. Déclarations de montage (par unité)
        const quantity = parseInt(product.quantity) || 1;
        for (let unitIndex = 0; unitIndex < quantity; unitIndex++) {
          const unitNumber = unitIndex + 1;
          const baseName = product.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'product';
          const productId = product.productId || product.uuid || product.id || `${baseName}_${productIndex}`;
          const declarationId = `${productId}_unit_${unitNumber}`;
          
          const montageKey = `declarationMontageCarrossierPdf_${declarationId}`;
          
          if (dossier[montageKey]) {
            console.log(`✅ Trouvé déclaration montage: ${montageKey} = ${dossier[montageKey]}`);
            productPdfs.push({
              type: 'montage',
              title: `Déclaration de Montage - Porte #${unitNumber}`,
              url: dossier[montageKey],
              status: 'generated',
              icon: '🔧',
              unitNumber
            });
          }
        }
        
        // 3. Contrôles périodiques
        const controlePossibleKeys = [
          `controlePeriodique_${product.productId}`,
          `controlePeriodique_${product.uuid}`,
          `controlePeriodique_${product.id}`,
          `controlePeriodique_${productIndex}`,
          `controlePeriodique_product_${productIndex}`
        ];
        
        controlePossibleKeys.forEach(key => {
          if (dossier[key]) {
            console.log(`✅ Trouvé contrôle périodique: ${key} = ${dossier[key]}`);
            productPdfs.push({
              type: 'controle',
              title: 'Contrôle Périodique',
              url: dossier[key],
              status: 'available',
              icon: '🔍'
            });
          }
        });
        
        if (productPdfs.length > 0) {
          pdfs.push({
            productName: product.name,
            productIndex,
            pdfs: productPdfs
          });
        }
      });
    }
    
    console.log('🔍 PDFs trouvés au total:', pdfs);
    return pdfs;
  };

  const allPdfs = findAllPdfs();
  const totalPdfs = allPdfs.reduce((total, product) => total + product.pdfs.length, 0);

  // 🐛 TOUJOURS afficher la section maintenant
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 overflow-hidden">
      
      {/* Header - cliquable pour expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-indigo-100/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
              📄 Tous les PDFs du dossier
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">
              {totalPdfs > 0 ? `${totalPdfs} document${totalPdfs > 1 ? 's' : ''} disponible${totalPdfs > 1 ? 's' : ''}` : 'Aucun PDF trouvé'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">
              📧 {allPdfs.filter(p => p.pdfs.some(pdf => pdf.status === 'sent')).length} envoyé{allPdfs.filter(p => p.pdfs.some(pdf => pdf.status === 'sent')).length > 1 ? 's' : ''}
            </span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              🔧 {allPdfs.filter(p => p.pdfs.some(pdf => pdf.status === 'generated')).length} généré{allPdfs.filter(p => p.pdfs.some(pdf => pdf.status === 'generated')).length > 1 ? 's' : ''}
            </span>
          </div>
          
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Contenu déroulant */}
      {isExpanded && (
        <div className="border-t border-indigo-200 bg-white/60">
          
          {/* 🐛 ZONE DEBUG - Affichage des clés */}
          <div className="px-4 sm:px-6 py-4 bg-yellow-50 border-b border-yellow-200">
            <h4 className="font-medium text-yellow-800 mb-2">🐛 Debug - Clés disponibles dans ce dossier :</h4>
            <div className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded max-h-32 overflow-y-auto">
              {Object.keys(dossier).map(key => (
                <div key={key} className="mb-1">
                  <strong>{key}:</strong> {typeof dossier[key] === 'string' && dossier[key].length > 50 
                    ? `${dossier[key].substring(0, 50)}...` 
                    : String(dossier[key])}
                </div>
              ))}
            </div>
          </div>
          
          {totalPdfs > 0 ? (
            <div className="p-4 sm:p-6 space-y-4">
              {allPdfs.map((productData, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  
                  {/* Header produit */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      🛠️ {productData.productName}
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {productData.pdfs.length} PDF{productData.pdfs.length > 1 ? 's' : ''}
                      </span>
                    </h4>
                  </div>
                  
                  {/* Liste des PDFs */}
                  <div className="p-4 space-y-3">
                    {productData.pdfs.map((pdf, pdfIndex) => (
                      <div
                        key={pdfIndex}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{pdf.icon}</span>
                          <div>
                            <h5 className="font-medium text-gray-900 text-sm">{pdf.title}</h5>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                pdf.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                pdf.status === 'generated' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {pdf.status === 'sent' ? '📧 Envoyé par mail' :
                                 pdf.status === 'generated' ? '🔧 Généré' :
                                 '📄 Disponible'}
                              </span>
                              {pdf.unitNumber && (
                                <span className="text-xs text-gray-500">
                                  Porte #{pdf.unitNumber}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => onPdfPreview(pdf.url, pdf.title, productData.productName)}
                            className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Voir
                          </button>
                          
                          <a
                            href={pdf.url}
                            download
                            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="hidden sm:inline">Télécharger</span>
                            <span className="sm:hidden">DL</span>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-4xl mb-3">📄</div>
                <h4 className="font-medium text-gray-900 mb-2">Aucun PDF disponible</h4>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  Les PDFs apparaîtront ici une fois les déclarations générées ou les documents envoyés par mail.
                </p>
                <div className="mt-4 text-xs text-gray-500">
                  <strong>Recherche effectuée pour :</strong> déclarations CE, déclarations de montage, contrôles périodiques
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PdfSection;